import { useQuery } from '@apollo/client';
import { AddCircle, DeviceHub } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { DeviceGroupsOrderBy } from '~/generated/graphql';
import { useRowSelectionMode } from '~/hooks/data-grid';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NewDeviceGroupDialog } from '../components';
import { NetworkDeviceGroupsDocument } from '../queries/list.generated';
import { BulkActions, Table } from './components';

export const DeviceGroupList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { selectedIds, setSelectedIds } = useRowSelectionMode();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { orderBy } = useOrderByParams<DeviceGroupsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(NetworkDeviceGroupsDocument, {
    variables: { networkId: currentNetwork.id, search, ...(orderBy && { orderBy }), page, perPage },
    onCompleted: (data) => handleOutOfBounds(data.network?.deviceGroups.pageInfo),
  });

  const [newDeviceGroupOpen, setNewDeviceGroupOpen] = useState(false);

  const newDeviceGroupButton = (
    <Button
      color="primary"
      onClick={() => setNewDeviceGroupOpen(true)}
      startIcon={<AddCircle />}
      variant="contained"
    >
      {t('pages.device_groups.buttons.new')}
    </Button>
  );

  const totalCount = useTotalCount(data?.network?.deviceGroups.pageInfo.nodesCount);

  const deviceGroups = useMemo(() => data?.network?.deviceGroups.nodes ?? [], [data]);

  const canDestroySelected = useMemo(() => {
    return selectedIds.every((selectedId) => {
      const deviceGroup = deviceGroups.find((group) => group.id === selectedId);
      return deviceGroup?.canDestroy.value;
    });
  }, [selectedIds, deviceGroups]);

  if (!loading && data?.network == null) return <Navigate replace to="/not-found" />;

  return (
    <>
      <Toolbar
        actions={newDeviceGroupButton}
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<DeviceHub />}
        titleText={t('pages.device_groups.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar
                placeholder={t('pages.device_groups.search_placeholder')}
                search={search}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions canDestroySelected={canDestroySelected} selectedIds={selectedIds} />
                ) : null}
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={totalCount}
                  thing="common.pluralize.device_group_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  deviceGroups={deviceGroups}
                  loading={loading}
                  onCheck={setSelectedIds}
                  selectedIds={selectedIds}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      {newDeviceGroupOpen && (
        <NewDeviceGroupDialog fullWidth onClose={() => setNewDeviceGroupOpen(false)} open />
      )}
    </>
  );
};
